import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SiteHeader } from "../components/layout/SiteHeader";
import { SiteFooter } from "../components/layout/SiteFooter";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../auth/AuthContext";
import { logEvent } from "../utils/analytics";
import { FaqAccordion } from "../components/shared/FaqAccordion";

const CONTAINERS = [
  { name: "20ft Standard",  cbm: 25.0, maxWeight: 21800, dims: "5.9m × 2.35m × 2.39m" },
  { name: "40ft Standard",  cbm: 55.0, maxWeight: 26700, dims: "12.03m × 2.35m × 2.39m" },
  { name: "40ft High Cube", cbm: 76.0, maxWeight: 26460, dims: "12.03m × 2.35m × 2.69m" },
  { name: "20ft High Cube", cbm: 37.0, maxWeight: 21800, dims: "5.9m × 2.35m × 2.69m" },
  { name: "45ft High Cube", cbm: 86.0, maxWeight: 27700, dims: "13.55m × 2.35m × 2.69m" },
];

// Volumetric weight rates by freight mode
// Sea (LCL): W/M rate — 1 m³ = 1,000 kg
// Air: IATA standard — 6,000 cm³/kg → 1 m³ = 167 kg
// Road: standard dimensional weight — 3,000 cm³/kg → 1 m³ = 333 kg
const VOL_RATE = { sea: 1000, air: 167, road: 333 };

const EMPTY_ITEM = { name: "", length: "", width: "", height: "", weight: "", quantity: "" };

const FIELD_LABELS = {
  length: "Length",
  width: "Width",
  height: "Height",
  weight: "Weight (kg)",
  quantity: "Quantity",
};

function calcItemCbm(item, unit) {
  const f = unit === "in" ? 0.0254 : 0.01;
  const l = parseFloat(item.length) * f;
  const w = parseFloat(item.width)  * f;
  const h = parseFloat(item.height) * f;
  const q = parseInt(item.quantity, 10) || 0;
  if (!l || !w || !h || !q) return 0;
  return l * w * h * q;
}

function calcItemWeight(item) {
  return (parseFloat(item.weight) || 0) * (parseInt(item.quantity, 10) || 0);
}

export function CbmCalculatorPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [unit, setUnit]       = useState("cm");
  const [mode, setMode]       = useState("sea"); // sea | air
  const [items, setItems]     = useState([{ ...EMPTY_ITEM }]);

  const updateItem = (idx, field, val) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));

  const addItem    = () => setItems((p) => [...p, { ...EMPTY_ITEM }]);
  const removeItem = (idx) => setItems((p) => p.filter((_, i) => i !== idx));

  const totalCbm = useMemo(
    () => items.reduce((s, it) => s + calcItemCbm(it, unit), 0),
    [items, unit]
  );
  const totalWeight = useMemo(
    () => items.reduce((s, it) => s + calcItemWeight(it), 0),
    [items]
  );

  // Sea freight: 1 CBM = 1,000 kg (W/M rate)
  // Air freight: 1 CBM = 167 kg  (6,000 cm³/kg standard)
  const volumetricWeight = totalCbm * VOL_RATE[mode];
  const chargeableWeight = Math.max(totalWeight, volumetricWeight);

  const hasData = totalCbm > 0;

  const loggedCalcRef = useRef(false);
  useEffect(() => {
    if (hasData && !loggedCalcRef.current) {
      loggedCalcRef.current = true;
      const t = setTimeout(() => {
        logEvent({ name: 'cbm_calculated', type: 'tool_use', meta: { mode, unit, itemCount: items.length } });
      }, 800); // debounce past active typing before counting it as a real calculation
      return () => clearTimeout(t);
    }
  }, [hasData]); // eslint-disable-line

  // Post-results CTA only: user already has real, calculated cargo, so a
  // logged-in user goes straight to console (uncapped) rather than through
  // Beyond CBM's capped input form again. Reuses the exact same
  // beyond_cbm_prefill + initialData handoff BeyondCbmPage's "Run in
  // console" button uses, so the calculated items land pre-filled in a
  // fresh simulation instead of an empty one. Containers are left empty —
  // the calculator never collects container types, only cargo dimensions.
  // (The previous "cbm_prefill" key this wrote was never read anywhere —
  // a dead handoff — replaced entirely rather than kept alongside this one.)
  const goToSimulation = () => {
    logEvent({ name: 'cbm_go_to_simulation', type: 'cta', meta: { authenticated: isAuthenticated } });

    if (isAuthenticated) {
      const consoleForm = {
        palletization: false,
        samePackageStackingOnly: false,
        containers: [],
        pallets: [],
        packages: items
          .filter((it) => calcItemCbm(it, unit) > 0)
          .map((it, i) => ({
            name: it.name || `Item ${i + 1}`,
            length: parseFloat(it.length) * (unit === "in" ? 2.54 : 1) / 100,
            width: parseFloat(it.width) * (unit === "in" ? 2.54 : 1) / 100,
            height: parseFloat(it.height) * (unit === "in" ? 2.54 : 1) / 100,
            maxStackingWeight: parseFloat(it.weight) || 0,
            maxStackingCount: 5,
            orientationRestricted: false,
            refrigerated: false,
            quantity: parseInt(it.quantity, 10) || 1,
          })),
      };
      sessionStorage.setItem("beyond_cbm_prefill", JSON.stringify(consoleForm));
      navigate("/console");
    } else {
      navigate("/3d-cargo-load-simulator");
    }
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-dark text-white">
      <Helmet>
        <title>Free CBM Calculator — Container Volume & Packing Optimiser | Feuteuristix</title>
        <meta name="description" content="Calculate CBM instantly. Find the cheapest container combination for your cargo — 20ft, 40ft, 40HQ. Free tool, no signup required. Includes LCL vs FCL guidance." />
        <meta property="og:title" content="Free CBM Calculator — Container Volume & Packing Optimiser | Feuteuristix" />
        <meta property="og:description" content="Calculate CBM instantly. Find the cheapest container combination for your cargo — 20ft, 40ft, 40HQ. Free tool, no signup required. Includes LCL vs FCL guidance." />
        <meta property="og:url" content="https://feuteuristix.com/cbm-calculator" />
        <meta name="twitter:title" content="Free CBM Calculator — Container Volume & Packing Optimiser | Feuteuristix" />
        <meta name="twitter:description" content="Calculate CBM instantly. Find the cheapest container combination for your cargo — 20ft, 40ft, 40HQ. Free tool, no signup required. Includes LCL vs FCL guidance." />
        <link rel="canonical" href="https://feuteuristix.com/cbm-calculator" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is CBM in shipping?",
              "acceptedAnswer": { "@type": "Answer", "text": "CBM stands for Cubic Metre — the standard unit used in international freight to measure shipment volume. One CBM equals 100cm × 100cm × 100cm. Freight forwarders use CBM to calculate rates and determine LCL vs FCL shipment type." }
            },
            {
              "@type": "Question",
              "name": "How do you calculate CBM for shipping?",
              "acceptedAnswer": { "@type": "Answer", "text": "CBM = (Length × Width × Height) ÷ 1,000,000 when dimensions are in centimetres. For example, a carton of 50cm × 40cm × 30cm = 0.06 CBM. Multiply by quantity to get total shipment CBM." }
            },
            {
              "@type": "Question",
              "name": "How many CBM is a 20ft container?",
              "acceptedAnswer": { "@type": "Answer", "text": "A standard 20ft shipping container holds approximately 25–28 CBM of usable cargo space (internal dimensions: 5.9m × 2.35m × 2.39m). The theoretical volume is ~33 CBM but structural elements reduce practical capacity." }
            },
            {
              "@type": "Question",
              "name": "How many CBM is a 40ft container?",
              "acceptedAnswer": { "@type": "Answer", "text": "A standard 40ft container holds approximately 55 CBM (12.03m × 2.35m × 2.39m). A 40ft High Cube holds approximately 76 CBM due to its extra 30cm of height." }
            },
            {
              "@type": "Question",
              "name": "When does FCL become cheaper than LCL?",
              "acceptedAnswer": { "@type": "Answer", "text": "FCL (Full Container Load) typically becomes more cost-effective than LCL (Less than Container Load) when your shipment exceeds 14–15 CBM. Below this threshold, LCL consolidation usually offers better rates. The exact break-even point varies by trade lane and carrier." }
            },
            {
              "@type": "Question",
              "name": "What is chargeable weight in shipping?",
              "acceptedAnswer": { "@type": "Answer", "text": "Chargeable weight is the higher of actual weight vs volumetric weight. For sea freight (LCL), 1 CBM = 1,000 kg. For air freight, 1 CBM = 167 kg. For road freight, 1 CBM = 333 kg. Carriers charge whichever is greater, so bulky lightweight cargo often pays on volume." }
            }
          ]
        })}</script>
      </Helmet>
      <SiteHeader />

      <main className="flex-1 pt-[76.8px] pb-16">
        {/* Hero */}
        <section className="bg-gradient-to-b from-background-dark to-slate-950/60 py-14">
          <div className="mx-auto max-w-4xl px-6 md:px-8 text-center">
            <h1 className="mt-4 text-4xl font-black leading-tight md:text-5xl">
              Free CBM Calculator
            </h1>
            <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
              Calculate the cubic metre volume of your shipment instantly. See which containers fit, check weight limits, and find the optimal packing plan — all in one place.
            </p>
          </div>
        </section>

        {/* Tool */}
        <section className="mx-auto max-w-4xl px-6 md:px-8 py-10">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 md:p-8">

            {/* Toggles row */}
            <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-lg font-black text-white">Enter your items</h2>
              <div className="flex items-center gap-3 flex-wrap">
                {/* Freight mode */}
                <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-slate-900 p-1">
                  {[["sea", "Sea freight"], ["air", "Air freight"], ["road", "Road freight"]].map(([m, label]) => (
                    <button
                      key={m}
                      onClick={() => setMode(m)}
                      className={`rounded-md px-3 py-1.5 text-sm font-bold transition ${
                        mode === m ? "bg-primary text-white" : "text-slate-400 hover:text-white"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {/* Unit */}
                <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-slate-900 p-1">
                  {["cm", "in"].map((u) => (
                    <button
                      key={u}
                      onClick={() => setUnit(u)}
                      className={`rounded-md px-3 py-1.5 text-sm font-bold transition ${
                        unit === u ? "bg-primary text-white" : "text-slate-400 hover:text-white"
                      }`}
                    >
                      {u === "cm" ? "cm" : "inches"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Item rows — horizontally scrollable on small screens */}
            <div className="overflow-x-auto -mx-2 px-2">
              <div className="min-w-[780px] space-y-3">
                {/* Header */}
                <div className="grid grid-cols-[160px_80px_80px_80px_90px_70px_80px_32px] gap-3 px-1">
                  {["Item name", `L (${unit})`, `W (${unit})`, `H (${unit})`, "Wt (kg)", "Qty", "CBM", ""].map((h) => (
                    <span key={h} className="text-xs font-bold uppercase tracking-widest text-slate-500">{h}</span>
                  ))}
                </div>

                {items.map((item, idx) => {
                  const itemCbm = calcItemCbm(item, unit);
                  return (
                    <div key={idx} className="grid grid-cols-[160px_80px_80px_80px_90px_70px_80px_32px] gap-3 items-center">
                      <input
                        aria-label={`Item ${idx + 1} name`}
                        className="h-10 rounded-lg border border-white/10 bg-slate-900 px-3 text-sm text-white placeholder:text-slate-600 focus:border-primary/60 focus:outline-none"
                        placeholder={`Item ${idx + 1}`}
                        value={item.name}
                        onChange={(e) => updateItem(idx, "name", e.target.value)}
                      />
                      {["length", "width", "height", "weight", "quantity"].map((field) => (
                        <input
                          key={field}
                          type="number"
                          min="0"
                          aria-label={`Item ${idx + 1} ${FIELD_LABELS[field]}${field === "length" || field === "width" || field === "height" ? ` (${unit})` : ""}`}
                          className="h-10 rounded-lg border border-white/10 bg-slate-900 px-3 text-sm text-white placeholder:text-slate-600 focus:border-primary/60 focus:outline-none"
                          placeholder={field === "quantity" ? "1" : "0"}
                          value={item[field]}
                          onChange={(e) => updateItem(idx, field, e.target.value)}
                        />
                      ))}
                      <div className="h-10 flex items-center px-1">
                        <span className={`text-sm font-bold ${itemCbm > 0 ? "text-primary" : "text-slate-700"}`}>
                          {itemCbm > 0 ? itemCbm.toFixed(3) : "—"}
                        </span>
                      </div>
                      <button
                        onClick={() => removeItem(idx)}
                        disabled={items.length === 1}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 hover:bg-red-500/10 hover:text-red-400 transition disabled:opacity-0"
                      >
                        <span className="material-symbols-outlined text-[16px]">close</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {items.length < 20 && (
              <button
                onClick={addItem}
                className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-primary transition"
              >
                <span className="material-symbols-outlined text-[16px]">add</span>
                Add item type
              </button>
            )}

            {/* Results */}
            {hasData && (
              <div className="mt-8 space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: "Total CBM", value: `${totalCbm.toFixed(3)} m³`, highlight: true },
                    { label: "Total Weight", value: `${totalWeight.toLocaleString()} kg` },
                    { label: "Chargeable Weight", value: `${chargeableWeight.toLocaleString()} kg`, sub: chargeableWeight > totalWeight ? `volumetric is higher (${mode === "sea" ? "1 CBM = 1,000 kg" : mode === "air" ? "1 CBM = 167 kg" : "1 CBM = 333 kg"})` : "actual weight is higher" },
                  ].map((s) => (
                    <div key={s.label} className={`rounded-xl border p-4 text-center ${s.highlight ? "border-primary/40 bg-primary/10" : "border-white/10 bg-white/[0.03]"}`}>
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{s.label}</p>
                      <p className={`mt-1 text-2xl font-black ${s.highlight ? "text-primary" : "text-white"}`}>{s.value}</p>
                      {s.sub && <p className="text-[11px] text-slate-500 mt-0.5">{s.sub}</p>}
                    </div>
                  ))}
                </div>

                {/* Container fit */}
                <div>
                  <h3 className="mb-3 text-sm font-bold uppercase tracking-widest text-slate-400">Container Fit</h3>
                  <div className="space-y-2">
                    {CONTAINERS.map((c) => {
                      const rawPct = (totalCbm / c.cbm) * 100;
                      const pct = Math.min(rawPct, 100);
                      const fits = totalCbm <= c.cbm && totalWeight <= c.maxWeight;
                      const overweight = totalCbm <= c.cbm && totalWeight > c.maxWeight;
                      const tooLarge   = totalCbm > c.cbm;
                      return (
                        <div key={c.name} className={`rounded-xl border px-4 py-3 ${fits ? "border-emerald-500/30 bg-emerald-500/5" : tooLarge ? "border-red-500/20 bg-red-500/[0.03]" : "border-white/8 bg-white/[0.02]"}`}>
                          <div className="flex items-center gap-4">
                            <div className="w-36 shrink-0">
                              <p className="text-sm font-bold text-white">{c.name}</p>
                              <p className="text-[11px] text-slate-500">{c.cbm} m³ · {(c.maxWeight / 1000).toFixed(1)}t max</p>
                            </div>
                            <div className="flex-1">
                              <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                                <div
                                  className={`h-full rounded-full transition-all duration-500 ${tooLarge ? "bg-red-500" : fits ? "bg-emerald-400" : "bg-slate-600"}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                            <div className="w-16 text-right">
                              <p className={`text-sm font-bold ${fits ? "text-emerald-400" : tooLarge ? "text-red-400" : "text-slate-500"}`}>
                                {rawPct.toFixed(0)}%
                              </p>
                            </div>
                            <div className="w-28 shrink-0 text-right">
                              {fits       && <span className="rounded-full bg-emerald-500/20 px-2.5 py-0.5 text-xs font-bold text-emerald-400">Fits ✓</span>}
                              {overweight && <span className="rounded-full bg-yellow-500/20 px-2.5 py-0.5 text-xs font-bold text-yellow-400">Overweight</span>}
                              {tooLarge   && <span className="rounded-full bg-red-500/20 px-2.5 py-0.5 text-xs font-bold text-red-400">Over capacity</span>}
                            </div>
                          </div>
                          {tooLarge && (
                            <p className="mt-1.5 text-[11px] text-red-400/70">Cargo volume exceeds this container's capacity</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Upsell */}
                <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-base font-black text-white">
                        Is this the cheapest way to ship this cargo?
                      </p>
                      <p className="mt-1 text-sm text-slate-300 max-w-md">
                        Our engine simulates the full packing plan for every container combination — 2×20ft, 1×40ft HC, 1×40ft Standard — and picks the one that ships everything for the least possible cost. See the 3D plan free.
                      </p>
                    </div>
                    <button
                      onClick={goToSimulation}
                      className="shrink-0 flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-600/20 transition hover:brightness-110 active:scale-95"
                    >
                      Find cheapest, tightest pack →
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    {isAuthenticated
                      ? 'Runs in your console, saved to your simulation history'
                      : 'Free signup · 10 expanded simulations, no card required'}
                  </p>
                </div>
              </div>
            )}

          </div>
        </section>


        {/* Reference chart — kept as an always-visible card, not an accordion row,
            since it's a lookup table rather than a question/answer. Own distinct
            background (lighter tint than the FAQ's bg-black/20 below) so each
            section reads as a separate "chapter" rather than one long scroll. */}
        <section className="bg-white/[0.025] border-y border-white/5 py-10">
          <div className="mx-auto max-w-4xl px-6 md:px-8">
          <article id="container-dimensions" className="scroll-mt-28">
            <h2 className="text-2xl font-black text-white mb-4">Standard container CBM capacity chart</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-white/10">
                    {["Container Type", "Internal Length", "Internal Width", "Internal Height", "Volume (CBM)", "Max Payload"].map((h) => (
                      <th key={h} className="pb-3 pr-6 text-xs font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  {[
                    ["20ft Standard",   "5.90 m",  "2.35 m", "2.39 m", "25.0 m³", "21,800 kg"],
                    ["40ft Standard",   "12.03 m", "2.35 m", "2.39 m", "55.0 m³", "26,700 kg"],
                    ["40ft High Cube",  "12.03 m", "2.35 m", "2.69 m", "76.0 m³", "26,460 kg"],
                    ["20ft High Cube",  "5.90 m",  "2.35 m", "2.69 m", "37.0 m³", "21,800 kg"],
                    ["45ft High Cube",  "13.55 m", "2.35 m", "2.69 m", "86.0 m³", "27,700 kg"],
                    ["LCL breakpoint",  "—",       "—",      "—",      "≥ 15 m³", "—"],
                  ].map(([type, ...rest]) => (
                    <tr key={type} className="border-b border-white/5">
                      <td className="py-3 pr-6 font-semibold text-white">{type}</td>
                      {rest.map((v, i) => <td key={i} className="py-3 pr-6">{v}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
          </div>
        </section>

        {/* FAQ — distinct, slightly darker section background so it reads as
            supporting content rather than competing with the tool above it. */}
        <section className="bg-black/20 border-y border-white/5 py-12">
          <div className="mx-auto max-w-4xl px-6 md:px-8">
            <h2 className="text-xl font-black text-white mb-5">Frequently asked questions</h2>
            <FaqAccordion
              items={[
                {
                  id: "what-is-cbm",
                  question: "What is CBM in shipping?",
                  answer: (
                    <>
                      <p>
                        CBM stands for <strong className="text-white">Cubic Metre</strong> — the standard unit used in international freight to measure the volume of a shipment. Freight forwarders, carriers, and customs agents all use CBM to calculate rates, allocate space in containers, and determine whether cargo qualifies for LCL (Less than Container Load) or FCL (Full Container Load) shipment.
                      </p>
                      <p className="mt-3">
                        One CBM equals one cubic metre of space: 100 cm × 100 cm × 100 cm. A standard 20ft shipping container holds approximately 25–28 CBM of usable cargo space (the theoretical maximum is ~33 CBM, but structural elements and loading constraints reduce the practical capacity).
                      </p>
                    </>
                  ),
                },
                {
                  id: "how-to-calculate-cbm",
                  question: "How to calculate CBM for a carton",
                  answer: (
                    <>
                      <p>The formula is straightforward:</p>
                      <div className="my-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 font-mono text-white text-xs md:text-sm">
                        CBM = (Length × Width × Height) ÷ 1,000,000
                        <span className="ml-4 text-slate-500">(if dimensions are in cm)</span>
                      </div>
                      <p>
                        For a carton measuring 50 cm × 40 cm × 30 cm:
                        50 × 40 × 30 = 60,000 cm³ = <strong className="text-white">0.06 CBM</strong> per carton.
                      </p>
                      <p className="mt-3">
                        For a shipment of 48 such cartons: 48 × 0.06 = <strong className="text-white">2.88 CBM total</strong>. This shipment would comfortably fit in a 20ft container (25 CBM capacity) at about 11.5% fill — or be consolidated as LCL freight.
                      </p>
                    </>
                  ),
                },
                {
                  id: "chargeable-weight",
                  question: "CBM vs chargeable weight — which one do you pay for?",
                  answer: (
                    <>
                      <p>
                        In freight, carriers charge based on whichever is higher: the <strong className="text-white">actual weight</strong> of the cargo or its <strong className="text-white">volumetric (dimensional) weight</strong>. The conversion rate differs by mode:
                      </p>
                      <ul className="mt-3 space-y-2">
                        <li><strong className="text-white">Sea freight (LCL):</strong> 1 CBM = 1,000 kg — the W/M (Weight/Measurement) rate. One freight ton equals 1 m³ or 1,000 kg, whichever is greater. So a 2 CBM shipment weighing 500 kg is charged at 2,000 kg.</li>
                        <li><strong className="text-white">Air freight:</strong> 1 CBM = 167 kg (6,000 cm³ per kg standard). A 2 CBM shipment has a dimensional weight of 334 kg — if the actual weight is less, you pay for 334 kg.</li>
                        <li><strong className="text-white">Road freight:</strong> 1 CBM = 333 kg (3,000 cm³ per kg standard). Used by most LTL and FTL operators in India and Europe. A 2 CBM shipment has a dimensional weight of 667 kg — bulky, light cargo almost always charges on volume.</li>
                      </ul>
                      <p className="mt-3">
                        Road and air freight dimensional weight penalties make accurate CBM calculation especially critical — bulky, lightweight cargo (furniture, foam, packaging) is almost always charged on dimensional weight, not actual weight.
                      </p>
                    </>
                  ),
                },
                {
                  id: "lcl-vs-fcl",
                  question: "LCL vs FCL: when does FCL become cheaper?",
                  answer: (
                    <>
                      <p>
                        As a rule of thumb, <strong className="text-white">FCL (Full Container Load)</strong> becomes cost-effective once your cargo exceeds approximately 14–15 CBM for a 20ft container, or when you need full container control for fragile, high-value, or time-sensitive goods. Below that threshold, <strong className="text-white">LCL (Less than Container Load)</strong> consolidation typically offers better rates.
                      </p>
                      <p className="mt-3">
                        The break-even point varies by trade lane, carrier, and season. On the India–Europe corridor, FCL typically becomes cheaper around 12–14 CBM. On Asia–USA lanes, the threshold is often 10–12 CBM due to higher LCL surcharges. Always compare FCL and LCL quotes for shipments in the 8–20 CBM range.
                      </p>
                    </>
                  ),
                },
              ]}
            />
          </div>
        </section>

        {/* Beyond CBM — single, merged bottom CTA. Used to be two cards: a
            "Did you know?" nudge shown only pre-data (with the flashy
            glow-blob background) and this plainer one shown always. Merged
            into one so the page doesn't stack two asks with the same
            destination — this one carries both the visual treatment and the
            combined copy, and is always visible regardless of scroll
            position or calculator state.
            Always routes to the Beyond CBM page itself, never /console —
            this is a general awareness CTA, not a data handoff, so it's the
            same destination for everyone; the post-results Upsell card
            above handles the auth-aware, data-carrying handoff instead. */}
        <section className="mx-auto max-w-4xl px-6 md:px-8 pt-14 pb-8">
          <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-slate-900 via-primary/5 to-slate-900 p-8">
            {/* Glow blobs */}
            <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-primary/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-cyan-500/15 blur-3xl" />

            <div className="relative z-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Beyond CBM</p>
                <h2 className="mt-1 text-lg font-black text-white leading-snug md:text-xl">
                  CBM tells you the volume.<br className="hidden md:block" />
                  <span className="bg-gradient-to-r from-primary via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                    Not which containers to book.
                  </span>
                </h2>
                <p className="mt-2 text-sm text-slate-400 max-w-md">
                  <strong className="text-white">2×20ft vs 1×40ft HC vs 1×40ft Standard</strong> — same cargo, vastly different cost, and that's where money gets left on the table. Our engine computes the cheapest combination and the tightest packing plan simultaneously — then shows you every carton in 3D.
                </p>
              </div>
              <Link
                to="/3d-cargo-load-simulator"
                onClick={() => logEvent({ name: 'cbm_open_beyond_cbm', type: 'cta' })}
                className="shrink-0 flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-primary/30 transition hover:brightness-110 active:scale-95"
              >
                Open Beyond CBM
                <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
